import { Box, Group, Text } from '@mantine/core';
import { useManualReconciliation } from 'api';
import type { Id } from 'api/models';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { map, omit, pipe, prop, sum } from 'remeda';
import CustomTable from 'shared/components/CustomTable';
import { Button } from 'shared/io';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { sortByDate } from 'shared/utils/date';
import type { Invoice } from 'types/store/invoice-state';
import type { PaymentHistory } from 'types/store/transaction-state';
import { InvoiceTableItem } from './InvoiceTableItem';

const InvoiceTable = CustomTable(InvoiceTableItem);

function buildHeaders(t: (key: string) => string) {
  return [
    { title: t(i18nKeys.FORM.REFERENCE) },
    { title: t(i18nKeys.ISSUE_DATE) },
    { title: t(i18nKeys.FORM.CHOOSE_INVOICES.REMAINING_AMOUNT) },
    { title: t(i18nKeys.WITH_FEES) },
    { title: t(i18nKeys.AMOUNT) },
  ];
}

function getInitialAmounts(invoices: Array<Invoice>, totalAssignableAmount: number) {
  const [finalAmounts] = sortByDate(invoices, 'issue_date', 'oldestFirst').reduce(
    ([amounts, remainingAssignableAmount], invoice) => {
      const amount = Math.min(remainingAssignableAmount, Number(invoice.remaining_balance));

      const fixedPrecisionAmount = Number(amount.toFixed(2));

      return [
        {
          ...amounts,
          [invoice.id]: String(fixedPrecisionAmount),
        },
        remainingAssignableAmount - fixedPrecisionAmount,
      ] as [Record<Id, string>, number];
    },
    [{}, totalAssignableAmount] as [Record<Id, string>, number],
  );

  return finalAmounts;
}

function handleSetAmount(
  invoiceId: Id,
  _newAmount: string,
  amounts: Record<Id, string>,
  invoices: Array<Invoice>,
  totalAssignableAmount: number,
  setAmounts: React.Dispatch<React.SetStateAction<Record<Id, string>>>,
) {
  // Allow fully erasing the field to avoid sticky leading 0s
  if (_newAmount === '') {
    setAmounts(prev => ({
      ...prev,
      [invoiceId]: '',
    }));
    return;
  }

  _newAmount = _newAmount.replaceAll(',', '.');
  const newAmount = Number(_newAmount);

  if (newAmount < 0) {
    setAmounts(prev => ({
      ...prev,
      [invoiceId]: '0',
    }));
    return;
  }

  const currentlyAssignedAmountWithoutThisField = pipe(
    amounts,
    omit([invoiceId]),
    Object.values,
    map(Number),
    sum,
    Number,
  );

  const amountMissingFromTotalWithoutThisField = Number(
    (totalAssignableAmount - currentlyAssignedAmountWithoutThisField).toFixed(2),
  );

  const invoice = invoices.find(inv => inv.id === invoiceId);
  if (invoice == null) return;

  const isNewAmountTooHigh =
    newAmount > amountMissingFromTotalWithoutThisField ||
    newAmount > invoice.remaining_balance_with_fees;

  const clampedAmount = Math.min(
    invoice.remaining_balance_with_fees,
    amountMissingFromTotalWithoutThisField,
  );

  setAmounts(prev => ({
    ...prev,
    [invoiceId]: isNewAmountTooHigh ? clampedAmount.toString() : newAmount.toString(),
  }));
}

interface ManualReconciliationModalProps {
  payments: Array<PaymentHistory>;
  invoices: Array<Invoice>;
  onSuccess: () => void;
}

export const ManualReconciliationModal = ({
  payments,
  invoices,
  onSuccess,
}: ManualReconciliationModalProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const formatter = useLocalizedCurrencyFormatter(false, company?.currency);
  const { mutate: reconcile, isPending } = useManualReconciliation();

  const totalAssignableAmount = pipe(
    payments,
    map(prop('remaining_balance')),
    map(Number),
    sum,
    Number,
  );

  const [assignedAmounts, setAssignedAmounts] = useState<Record<Id, string>>(() =>
    getInitialAmounts(invoices, totalAssignableAmount),
  );

  const currentlyAssignedAmount = pipe(assignedAmounts, Object.values, map(Number), sum, Number);
  const isSaturated = currentlyAssignedAmount === totalAssignableAmount;

  const handleReconciliation = () => {
    reconcile(
      {
        data: {
          invoices: Object.entries(assignedAmounts).map(([id, amount]) => ({
            id,
            amount: Number(amount),
          })),
          payment_ids: payments.map(payment => payment.id),
        },
      },
      {
        onSuccess: () => onSuccess(),
      },
    );
  };

  return (
    <Box p="space5">
      <InvoiceTable
        noMinHeight
        style={{ fontSize: '12px' }}
        headers={buildHeaders(t)}
        items={invoices}
        itemProps={{
          handleSetAmount: (id: any, amount: any) =>
            handleSetAmount(
              id,
              amount,
              assignedAmounts,
              invoices,
              totalAssignableAmount,
              setAssignedAmounts,
            ),
          amounts: assignedAmounts,
          disableButtons: isSaturated,
        }}
        noCheckbox
      />
      <Group justify="flex-end" gap={0} mt="space5" c="#343944">
        <Text fw="bold" size="xl" c={isSaturated ? 'purple.6' : 'warning.7'}>
          {formatter.format(currentlyAssignedAmount)}
        </Text>
        <Text fw="bold" size="xl" c={isSaturated ? 'purple.6' : 'inherit'}>
          {`/ ${formatter.format(totalAssignableAmount)}`}
        </Text>
      </Group>
      <Group justify="flex-end" gap={15}>
        <Button
          disabled={currentlyAssignedAmount === 0 || isPending}
          onClick={handleReconciliation}
        >
          {t(i18nKeys.RECONCILE)}
        </Button>
      </Group>
    </Box>
  );
};
